import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { studentAPI, sessionAPI } from '../services/api';
import ConflictResolution from './ConflictResolution';
import { useAuth } from '../contexts/AuthContext';

const StudentEditForm = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [error, setError] = useState('');
  const [classrooms, setClassrooms] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const loadStudent = useCallback(async () => {
    if (isNew) {
      try {
        const res = await sessionAPI.getCurrentSession();
        setSessionId(res.data.id);
        const classRes = await sessionAPI.getClassrooms(res.data.id);
        setClassrooms(Array.isArray(classRes.data.results || classRes.data) ? (classRes.data.results || classRes.data) : []);
      } catch (_) { /* no session yet */ }
      setLoading(false);
      return;
    }
    try {
      const response = await studentAPI.getStudent(id);
      const data = response.data;
      setStudent(data);
      setFormData(data);
      setSessionId(data.session);
      if (data.session) {
        try {
          const classRes = await sessionAPI.getClassrooms(data.session);
          const list = classRes.data.results || classRes.data || [];
          setClassrooms(Array.isArray(list) ? list : []);
        } catch (_) {
          setClassrooms([]);
        }
      }
    } catch (error) {
      console.error('Error loading student:', error);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    loadStudent();
  }, [loadStudent]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = isNew
        ? { ...formData, session: sessionId }
        : { ...formData, version: student.version };
      const res = isNew
        ? await studentAPI.createStudent(payload)
        : await studentAPI.updateStudent(id, payload);
      if (res && res.status >= 200 && res.status < 300) {
        setTimeout(() => navigate(`/students/${(id || res.data?.id) ?? ''}`), 0);
        return;
      }
    } catch (err) {
      if (!isNew && err.response?.data?.version) {
        setConflict({
          current_version: err.response.data.version,
          message: 'Record was modified by another user',
        });
        loadStudent();
      } else if (err.response || err.isAxiosError) {
        const detail = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        setError(`Error saving student: ${detail}`);
      }
      setSaving(false);
    }
  };

  const handleConflictResolve = (resolution, data) => {
    if (resolution === 'server') {
      setFormData(data);
      setStudent({ ...student, ...data });
    } else if (resolution === 'local') {
      // Retry save with current version
      handleSave();
    }
    setConflict(null);
  };

  if (loading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">{isNew ? 'Add Student' : 'Edit Student'}</Typography>
          <Box>
            {!isNew && student?.locked_by_email && student.locked_by_email !== user?.email && (
              <Chip
                label={`Locked by ${student.locked_by_email}`}
                color="warning"
                size="small"
              />
            )}
          </Box>
        </Box>

        {conflict && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {conflict.message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>Personal</Typography>
          <TextField label="First Name" value={formData.first_name || ''} onChange={(e) => handleChange('first_name', e.target.value)} fullWidth required />
          <TextField label="Last Name" value={formData.last_name || ''} onChange={(e) => handleChange('last_name', e.target.value)} fullWidth required />
          <TextField label="Date of Birth" type="date" value={typeof formData.date_of_birth === 'string' ? formData.date_of_birth.slice(0, 10) : (formData.date_of_birth || '')} onChange={(e) => handleChange('date_of_birth', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} required />
          <TextField label="Enrollment Date" type="date" value={typeof formData.enrollment_date === 'string' ? formData.enrollment_date.slice(0, 10) : (formData.enrollment_date || '')} onChange={(e) => handleChange('enrollment_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={formData.status || 'active'} onChange={(e) => handleChange('status', e.target.value)} label="Status">
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="graduated">Graduated</MenuItem>
              <MenuItem value="transferred">Transferred</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Service type</InputLabel>
            <Select value={formData.service_type || 'center_based'} onChange={(e) => handleChange('service_type', e.target.value)} label="Service type">
              <MenuItem value="center_based">Center-based</MenuItem>
              <MenuItem value="related_service">Related service</MenuItem>
              <MenuItem value="seit">SEIT</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Active</InputLabel>
            <Select value={formData.is_active !== false ? 'yes' : 'no'} onChange={(e) => handleChange('is_active', e.target.value === 'yes')} label="Active">
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Directory info opt-out</InputLabel>
            <Select value={formData.directory_info_opt_out ? 'yes' : 'no'} onChange={(e) => handleChange('directory_info_opt_out', e.target.value === 'yes')} label="Directory info opt-out">
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Address" value={formData.address || ''} onChange={(e) => handleChange('address', e.target.value)} fullWidth multiline minRows={1} />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>Parent / Guardian</Typography>
          <TextField label="Home phone" value={formData.home_phone || ''} onChange={(e) => handleChange('home_phone', e.target.value)} fullWidth />
          <TextField label="Parent phone" value={formData.parent_phone || ''} onChange={(e) => handleChange('parent_phone', e.target.value)} fullWidth />
          <TextField label="Mother cell" value={formData.mother_cell || ''} onChange={(e) => handleChange('mother_cell', e.target.value)} fullWidth />
          <TextField label="Father cell" value={formData.father_cell || ''} onChange={(e) => handleChange('father_cell', e.target.value)} fullWidth />
          <TextField label="Email" value={formData.email || ''} onChange={(e) => handleChange('email', e.target.value)} fullWidth type="email" />
          <TextField label="Parent email" value={formData.parent_email || ''} onChange={(e) => handleChange('parent_email', e.target.value)} fullWidth type="email" />
          <TextField label="Emergency contact" value={formData.emergency_contact || ''} onChange={(e) => handleChange('emergency_contact', e.target.value)} fullWidth />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>Program / Roster</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Class (center-based only; leave empty for related service)</InputLabel>
            <Select value={formData.class_num ?? ''} onChange={(e) => handleChange('class_num', e.target.value || null)} label="Class (center-based only; leave empty for related service)">
              <MenuItem value="">Related service (not on roster)</MenuItem>
              {classrooms.map((c) => (
                <MenuItem key={c.id} value={c.class_num}>{c.label || `${c.class_num} / ${c.class_size || '—'}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Funding code" value={formData.funding_code || ''} onChange={(e) => handleChange('funding_code', e.target.value)} fullWidth />
          <TextField label="District" value={formData.district || ''} onChange={(e) => handleChange('district', e.target.value)} fullWidth />
          <TextField label="School district" value={formData.school_district || ''} onChange={(e) => handleChange('school_district', e.target.value)} fullWidth />
          <TextField label="1:1 Aide" value={formData.aide_1to1 || ''} onChange={(e) => handleChange('aide_1to1', e.target.value)} fullWidth />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>Discharge</Typography>
          <TextField label="Discharge date" type="date" value={typeof formData.discharge_date === 'string' ? formData.discharge_date.slice(0, 10) : (formData.discharge_date || '')} onChange={(e) => handleChange('discharge_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Discharge notes" value={formData.discharge_notes || ''} onChange={(e) => handleChange('discharge_notes', e.target.value)} fullWidth multiline minRows={1} />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>Medical / Vaccines</Typography>
          <TextField label="Vaccines status" value={formData.vaccines_status || ''} onChange={(e) => handleChange('vaccines_status', e.target.value)} fullWidth />
          <TextField label="Vaccines last reviewed" type="date" value={typeof formData.vaccines_last_reviewed === 'string' ? formData.vaccines_last_reviewed.slice(0, 10) : (formData.vaccines_last_reviewed || '')} onChange={(e) => handleChange('vaccines_last_reviewed', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Vaccines notes" value={formData.vaccines_notes || ''} onChange={(e) => handleChange('vaccines_notes', e.target.value)} fullWidth multiline minRows={1} />
          <TextField label="Medical due date" type="date" value={typeof formData.medical_due_date === 'string' ? formData.medical_due_date.slice(0, 10) : (formData.medical_due_date || '')} onChange={(e) => handleChange('medical_due_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Medical start date" type="date" value={typeof formData.medical_start_date === 'string' ? formData.medical_start_date.slice(0, 10) : (formData.medical_start_date || '')} onChange={(e) => handleChange('medical_start_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Medical end date" type="date" value={typeof formData.medical_end_date === 'string' ? formData.medical_end_date.slice(0, 10) : (formData.medical_end_date || '')} onChange={(e) => handleChange('medical_end_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>SPED</Typography>
          <TextField label="SPED INDIV Code" value={formData.sped_indiv_code || ''} onChange={(e) => handleChange('sped_indiv_code', e.target.value)} fullWidth />

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, fontWeight: 600 }}>Notes</Typography>
          <TextField label="Notes" value={formData.notes || ''} onChange={(e) => handleChange('notes', e.target.value)} fullWidth multiline rows={3} />

          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate(id ? `/students/${id}` : '/students')}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <ConflictResolution
        open={!!conflict}
        conflict={conflict}
        onResolve={handleConflictResolve}
        onCancel={() => setConflict(null)}
        currentData={formData}
        serverData={student}
      />
    </Container>
  );
};

export default StudentEditForm;
